/**
 * TschaukoAgentRoot — App-level mount point for the Realtime-Voice agent
 * in tschepp-ar-web.
 *
 * The bottom layer (VoiceAgentController + CommandRegistry) is plain
 * TypeScript. This file is the React-side wiring: it creates one
 * singleton controller per App lifetime, provides it via
 * AgentSessionContext to all descendants, registers the app's command
 * vocabulary, and mounts the overlay UI.
 *
 * Why useRef-singleton instead of a module-level `new VoiceAgentController()`?
 *   - Easier to reason about lifecycle (created on first render, lives
 *     until the React tree unmounts which only happens on full page
 *     reload).
 *   - Test-friendly: each `<TschaukoAgentRoot>` gets its own controller,
 *     no cross-test bleed.
 *   - Hot-reload-friendly in dev: Vite HMR can replace the React tree;
 *     the previous controller's teardown runs naturally.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { installFlushTriggers } from './progressTracker'
import { startPoiWatcher } from './poiWatcher'
import type { ReactNode } from 'react'
import TschaukoVoiceOverlay from '../../components/TschaukoVoiceOverlay'
import { getConfig } from '../../config/project'
import { createCommandRegistry } from './CommandRegistry'
import { AgentSessionProvider, useAgentSession, useAgentCommand } from './AgentSessionContext'
import { AgentPersonaContext } from './personas/AgentPersonaContext'
import {
  AgentSessionManager,
  createTenantPersonaRegistry,
  type AgentPersonaRegistry,
} from './personas/AgentPersonaRegistry'
import { useTheme } from '../../theme'
import { navigate, usePathname } from '../routing'
import { getMapPath } from '../../config/project'
import { getLocationService } from '../../domain/LocationService'

/** Ship controller-internal errors to the existing log-api error session
 *  (the one main.tsx creates for window.onerror). Catch'd errors don't
 *  hit window.onerror naturally, so we post explicitly. */
const LOG_API = 'https://log-api.arkturian.com/api/v1/sessions'

async function ensureLogSession(): Promise<string> {
  const w = window as unknown as { __errorLogSessionId?: string }
  if (w.__errorLogSessionId) return w.__errorLogSessionId
  const sessionId = `tschepp-err-${Date.now()}`
  try {
    await fetch(LOG_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: sessionId,
        app_id: 'tschepp-ar-web',
        device_model: navigator.userAgent.slice(0, 80),
        os: navigator.platform,
      }),
    })
    w.__errorLogSessionId = sessionId
  } catch { /* noop */ }
  return sessionId
}

async function logVoiceAgentInfo(event: string, ctx?: Record<string, unknown>) {
  try {
    const sessionId = await ensureLogSession()
    await fetch(`${LOG_API}/${sessionId}/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entries: [{
          timestamp: new Date().toISOString(),
          log_type: 'Info',
          source: 'voice-agent',
          message: `[${event}]${ctx ? ' · ' + JSON.stringify(ctx) : ''}`,
          stack_trace: null,
          sequence: 0,
        }],
      }),
    })
  } catch { /* never let the logger fail us */ }
}

async function logVoiceAgentError(event: string, err: unknown, ctx?: Record<string, unknown>) {
  try {
    const sessionId = await ensureLogSession()
    const msg = err instanceof Error ? err.message : String(err)
    const stack = err instanceof Error ? err.stack : undefined
    await fetch(`${LOG_API}/${sessionId}/logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entries: [{
          timestamp: new Date().toISOString(),
          log_type: 'Error',
          source: 'voice-agent',
          message: `[${event}] ${msg}${ctx ? ' · ctx=' + JSON.stringify(ctx) : ''}`,
          stack_trace: stack || null,
          sequence: 0,
        }],
      }),
    })
  } catch { /* never let the logger fail us */ }
}

/** Expose the info-logger globally so framework-agnostic components
 *  (TschaukoPortrait, ThreeCanvas, etc.) can push lifecycle events to
 *  the same log-api session without taking a direct dependency on the
 *  voice-agent lib. */
;(window as unknown as { __voiceAgentLogInfo?: typeof logVoiceAgentInfo }).__voiceAgentLogInfo = logVoiceAgentInfo

export function TschaukoAgentRoot({ children }: { children: ReactNode }) {
  // Registry created once for the App lifetime.
  const registry = useMemo(() => createCommandRegistry(), [])

  // Install offline-queue flush triggers (online event + tab-visible +
  // 2s post-mount initial flush) so progress events queued during a
  // previous offline session get delivered as soon as we can. Runs once.
  useEffect(() => { installFlushTriggers() }, [])

  // POI-proximity watcher: passively subscribes to LocationService and
  // fires `poi_visited` progress events when the user comes within 30m
  // of any Track-30 waypoint. Silent when GPS isn't active — no
  // permission prompt from us, we wait for NavigatePage / Realtime
  // session to enable tracking. Cleanup on unmount.
  useEffect(() => {
    const stop = startPoiWatcher()
    return () => stop()
  }, [])

  // Auto-activate LocationService whenever a Realtime session goes
  // active on ANY route (drop-up sheet on /navigate-tile, /browse, /,
  // or the standalone /chat page). This is what lets the POI-watcher
  // hear position updates during a chat that happens on a non-map
  // page. `startTracking()` is idempotent so double-calls (map page
  // AND chat) are safe. We don't stopTracking on session close —
  // other consumers might still need it. Ref-counting is a future
  // refactor. See controllerRef declaration below for wiring.
  const gpsControllerRef = useRef<{ subscribe: (cb: () => void) => () => void; getSnapshot: () => { status: string } } | null>(null)

  // Figuren-Verwaltung statt Figuren-Wissen (Alex 2026-08-03): Der Root
  // kennt keine Instruktionen, Stimmen oder Werkzeuge mehr — er hält die
  // Registry des Mandanten und den Sitzungs-Manager, der je Figur genau
  // EINEN Controller lazy erzeugt. Eine neue Figur anzulegen heißt jetzt:
  // eine Klasse schreiben (siehe personas/TscheppaPersonas.ts), nicht
  // diesen Baum verbreitern.
  const managerRef = useRef<AgentSessionManager | null>(null)
  const personasRef = useRef<AgentPersonaRegistry | null>(null)
  if (managerRef.current === null) {
    personasRef.current = createTenantPersonaRegistry()
    managerRef.current = new AgentSessionManager(personasRef.current, {
      registry,
      onError: logVoiceAgentError,
      onInfo: logVoiceAgentInfo,
    })
  }
  const manager = managerRef.current
  const personas = personasRef.current!

  // Welche Figur gerade spricht. Hosts (z. B. /live-tschauka) setzen sie
  // über `useAgentPersonaSwitch`; der Wechsel beendet die alte Sitzung.
  const [activePersonaId, setActivePersonaId] = useState(() => manager.activePersonaId)
  const activatePersona = useCallback((id: string) => {
    setActivePersonaId(prev => {
      if (prev === id) return prev
      manager.activate(id)
      return manager.activePersonaId
    })
  }, [manager])

  const personaValue = useMemo(() => ({
    persona: personas.get(activePersonaId) ?? personas.default,
    personas,
    activatePersona,
  }), [activePersonaId, personas, activatePersona])

  const controller = manager.controllerFor(activePersonaId)
  gpsControllerRef.current = controller

  useEffect(() => {
    const c = gpsControllerRef.current
    if (!c) return
    let wasActive = false
    const unsub = c.subscribe(() => {
      const snap = c.getSnapshot()
      const active = snap.status !== 'idle' && snap.status !== 'closed' && snap.status !== 'error'
      if (active && !wasActive) {
        const ls = getLocationService()
        if (!ls.isTracking) ls.startTracking()
      }
      wasActive = active
    })
    return () => unsub()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePersonaId])


  return (
    <AgentPersonaContext.Provider value={personaValue}>
      {/* key: Figurenwechsel = anderer Controller = frischer Snapshot-Abo.
          Ohne key würde der Provider den alten Snapshot weiterreichen. */}
      <AgentSessionProvider key={activePersonaId} controller={controller} registry={registry}>
        <CommandHandlers />
        {children}
        <OverlayMount />
      </AgentSessionProvider>
    </AgentPersonaContext.Provider>
  )
}

/** Tschauko-app command vocabulary. Each handler translates a generic
 *  agent instruction into a concrete app side-effect. */
function CommandHandlers() {
  const { controller, minimize } = useAgentSession()
  // Pfad wird gebraucht, um zu entscheiden, ob eine echte Karte hinter dem
  // Overlay liegt (siehe show_location).
  const path = usePathname()

  /** Eine bereits gemountete Karte bekommt den Fokus als direktes App-
   *  Ereignis. Der fruehere Umweg ueber `navigate(?focus=...)` war fuer
   *  Seitenwechsel richtig, auf derselben Karte aber rennte er gegen URL-
   *  Konsumierung, POI-Hydration und den laufenden GPS-Follow-Modus. */
  const focusMountedMap = (waypointId: number): boolean => {
    if (path !== getMapPath()) return false
    minimize()
    window.dispatchEvent(new CustomEvent('tschepp:focus-poi', {
      detail: { waypointId, source: 'voice' },
    }))
    return true
  }

  // show_species — bot wants the user to see a species visually. INLINE
  // mode: render the SpeciesView inside the Tschauko overlay (host swaps
  // the transcript for it). The session stays in the foreground; no
  // page navigation.
  useAgentCommand<{ knowledge_id: number; name?: string }>(
    'show_species',
    async ({ knowledge_id, name }) => {
      console.debug('[command] show_species', knowledge_id, name)
      // In the iOS embed the native shell owns detail presentation.  Do not
      // open the Web dashboard/Infoboard in parallel; hand the stable
      // Knowledge reference to SwiftUI and only keep the Web overlay as a
      // fallback when no native host accepted it.
      const nativeAccepted = await window.tscheppaHostBridge?.presentKnowledge({
        trackId: 30,
        knowledgeId: knowledge_id,
        ...(name ? { name } : {}),
      })
      if (nativeAccepted) {
        controller.clearFocusedKnowledge()
        return
      }
      controller.setFocusedKnowledge(knowledge_id)
    },
  )

  // focus_map — bot mentions a station, e.g. "Der Tschaukofall ist
  // 26 m hoch...". The standalone /live host stays mounted and shows the
  // canonical Entdecken detail inline. Only its explicit map action may
  // leave the conversation; the other hosts retain their current behaviour.
  useAgentCommand<{ name: string; lat?: number; lon?: number; waypoint_id?: string | number }>(
    'focus_map',
    ({ name, lat, lon, waypoint_id }) => {
      const waypointId = Number(waypoint_id)
      if (!Number.isFinite(waypointId) || waypointId <= 0) {
        console.debug('[command] focus_map skipped (no waypoint_id)', name)
        return
      }
      console.debug('[command] focus_map →', name, 'wp:', waypointId)
      if (path === '/live') {
        controller.setFocusedLocation({
          name: name || 'Standort',
          lat: typeof lat === 'number' ? lat : 0,
          lon: typeof lon === 'number' ? lon : 0,
          waypointId,
        })
        return
      }
      if (focusMountedMap(waypointId)) return
      // Plain numeric waypoint id — NavigatePage parses it as Number().
      // Replace history entry so we don't pollute back-button on every
      // station mention.
      navigate(`/navigate-tile?focus=${waypointId}`, { replace: true })
    },
  )

  // show_expedition — der Führer öffnet das Entdeckerbuch der Kinder-
  // Expedition (Spec #4556 §23.2). BEWUSST das Buch statt des Fundorts:
  // Ein Kartenflug zum Geheimnis wäre der Spoiler, den die Spec verbietet
  // (§14.3) — das Buch zeigt Auftrag und Merkmale, gesucht wird draußen.
  useAgentCommand<Record<string, never>>(
    'show_expedition',
    () => {
      console.debug('[command] show_expedition')
      minimize()
      const aufDerKarte = path === '/navigate-tile' || path === '/navigate-3d'
      if (aufDerKarte) {
        window.dispatchEvent(new CustomEvent('tschepp:open-expedition'))
        return
      }
      // Von /chat, /live etc.: erst zur Karte, dann öffnen — der Listener
      // sitzt in NavigatePage und braucht den Mount.
      navigate('/navigate-tile', { replace: true })
      window.setTimeout(() => window.dispatchEvent(new CustomEvent('tschepp:open-expedition')), 900)
    },
  )

  // show_location — der Besucher hat nach einem Standort gefragt („wo ist das
  // Besucherzentrum?"). Antwort ist IMMER die echte App-Karte (Alex 2026-08-17:
  // der OSM-Kartenausschnitt im Overlay war ein Prototyp und ist abgeschaltet):
  // Overlay minimiert sich zur Kugel, die Karte fliegt zum Punkt. Ein Tipp auf
  // die Kugel holt das Gespraech zurueck — die Sitzung laeuft weiter. Auf /live
  // bleibt die Sitzung im Vordergrund und zeigt das Stationsdetail inline
  // (derselbe Weg wie focus_map). Ohne Wegpunkt gibt es nichts anzufliegen —
  // der Erzaehler beschreibt den Ort in Worten (Hints in where_is).
  useAgentCommand<{ name: string; lat?: number; lon?: number; waypoint_id?: string | number }>(
    'show_location',
    ({ name, lat, lon, waypoint_id }) => {
      if (typeof lat !== 'number' || typeof lon !== 'number') {
        console.debug('[command] show_location ohne Koordinaten', name)
        return
      }
      const wp = Number(waypoint_id)
      const wpId = Number.isFinite(wp) && wp > 0 ? wp : undefined
      if (wpId == null) {
        console.debug('[command] show_location ohne Wegpunkt — nur Sprache', name)
        return
      }
      if (path === '/live') {
        controller.setFocusedLocation({ name: name || 'Standort', lat, lon, waypointId: wpId })
        return
      }
      controller.clearFocusedLocation()
      if (focusMountedMap(wpId)) return
      minimize()
      navigate(`${getMapPath()}?focus=${wpId}`, { replace: true })
    },
  )

  return null
}

function OverlayMount() {
  const theme = useTheme()
  const path = usePathname()
  // Suppress the global drop-up on standalone agent routes. Each owns its
  // complete host surface; mounting the dashboard host too would render the
  // same App-scoped session twice.
  if (path === '/chat' || path === '/live') return null
  return <TschaukoVoiceOverlay dark={theme.skyMode === 'night'} />
}
